/**
 * Runtime script injection for GA4 and Microsoft Clarity.
 *
 * Scripts are NOT loaded until the user accepts the cookie-consent banner,
 * so no third-party cookies are set before consent.
 *
 * Clarity is only injected in production to avoid polluting analytics with
 * dev-session activity.
 */

let ga4Injected = false;
let clarityInjected = false;

/**
 * Inject the Google Analytics 4 gtag.js snippet and configure the
 * measurement ID. Safe to call multiple times — idempotent.
 */
export function injectGA4(): void {
  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
  if (!measurementId || ga4Injected) return;
  ga4Injected = true;

  try {
    // Initialise the gtag data layer.
    // IMPORTANT: gtag.js requires the special `arguments` object to be pushed
    // into dataLayer — pushing a plain array is silently ignored by Google's
    // library and results in "No data received".
    window.dataLayer = window.dataLayer ?? [];
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params, @typescript-eslint/no-explicit-any
      (window.dataLayer as any[]).push(arguments);
    };
    window.gtag("js", new Date());
    window.gtag("config", measurementId, {
      anonymize_ip: true,
      // Suppress automatic page_view so we fire it ourselves on route changes
      send_page_view: false,
    });

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);
  } catch { /* never block the user */ }
}

/**
 * Inject the Microsoft Clarity snippet.
 * Only runs in production and only once.
 */
export function injectClarity(): void {
  if (!import.meta.env.PROD) return;
  const projectId = import.meta.env.VITE_CLARITY_PROJECT_ID as string | undefined;
  if (!projectId || clarityInjected) return;
  clarityInjected = true;

  try {
    // Clarity self-contained initialisation (masks all inputs by default)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (function (c: any, l: Document, a: string, r: string, i: string) {
      c[a] = c[a] || function (...args: unknown[]) { (c[a].q = c[a].q || []).push(args); };
      const t = l.createElement(r) as HTMLScriptElement;
      t.async = true;
      t.src = "https://www.clarity.ms/tag/" + i;
      const y = l.getElementsByTagName(r)[0];
      y.parentNode?.insertBefore(t, y);
    })(window, document, "clarity", "script", projectId);

    // Ensure all text inputs are masked (in case project settings haven't been saved)
    if (typeof window.clarity === "function") {
      window.clarity("set", "maskAllInputs", true);
    }
  } catch { /* never block the user */ }
}

/**
 * Called when the user accepts the consent banner.
 * Injects both scripts and fires a page_view for the current page.
 */
export function loadAnalyticsAfterConsent(): void {
  injectGA4();
  injectClarity();

  // Fire the initial page_view now that gtag is available
  try {
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: window.location.pathname,
      });
    }
  } catch { /* ignore */ }
}

// Augment global window type for dataLayer
declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer: any[];
  }
}
